import { environment } from 'src/environments/environment';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { HttpService } from '../../services/http/http.service';
import { ApiSchema } from '../api-schema';
import { BootupData, BootupSchema } from './schemas/bootup';
import { ActivityFeedData, ActivityFeedSchema } from './schemas/activity-feed';
import { ApiResponseSchema } from '../api-response';
import { z } from 'zod';
import { GameId } from 'src/app/model/game/game-id';
import { FindMatchData, FindMatchSchema } from './schemas/find-match';
import { NullDataSchema } from './schemas/null-data';
import {
  LeaderboardPeriodType,
  LeaderboardType,
} from '../../services/leaderboards/models/leaderboard-metadata';
import { GameLeaderboardsData, GameLeaderboardsSchema } from './schemas/game-leaderboards';

export const WAGER_ENDPOINTS = {
  ACTIVITY_FEED: 'activity-feed',
  BOOTUP: 'bootup',
  FIND_MATCH: 'find-match',
  FINISH_ROUND: 'finish-round',
  GAME_LEADERBOARDS: 'game-leaderboards',
} as const;

export class WagerApi {
  private api: ApiSchema;

  private constructor(httpService: HttpService, analyticsService: AnalyticsService) {
    this.api = new ApiSchema(environment.wagerApi, httpService, analyticsService);
  }

  public static request(httpService: HttpService, analyticsService: AnalyticsService) {
    return new WagerApi(httpService, analyticsService);
  }

  public async activityFeed(gameId?: string): Promise<ActivityFeedData> {
    const ActivityFeedResponseSchema = ApiResponseSchema(ActivityFeedSchema);
    type ActivityFeedResponse = z.infer<typeof ActivityFeedResponseSchema>;

    const url = gameId
      ? `${WAGER_ENDPOINTS.ACTIVITY_FEED}?gameId=${gameId}`
      : WAGER_ENDPOINTS.ACTIVITY_FEED;
    const response = await this.api.get<ActivityFeedResponse>(url, ActivityFeedResponseSchema);
    return response.data;
  }

  public async bootup(): Promise<BootupData> {
    return this.api.get<BootupData>(WAGER_ENDPOINTS.BOOTUP, BootupSchema);
  }

  public async findMatch(
    gameId: GameId,
    tierId: string,
    depositId: string,
    tournamentId?: string,
  ): Promise<FindMatchData> {
    const FindMatchResponseSchema = ApiResponseSchema(FindMatchSchema);
    type FindMatchResponse = z.infer<typeof FindMatchResponseSchema>;

    const response = await this.api.post<
      FindMatchResponse,
      { gameId: GameId; tierId: string; depositId: string; tournamentId?: string }
    >(WAGER_ENDPOINTS.FIND_MATCH, FindMatchResponseSchema, {
      gameId,
      tierId,
      depositId,
      ...(tournamentId != null && { tournamentId }),
    });
    return response.data;
  }

  public async findOperatorMatch(
    gameId: GameId,
    tierId: string,
    idempotencyKey: string,
  ): Promise<FindMatchData> {
    const FindMatchResponseSchema = ApiResponseSchema(FindMatchSchema);
    type FindMatchResponse = z.infer<typeof FindMatchResponseSchema>;

    const response = await this.api.post<
      FindMatchResponse,
      { gameId: GameId; tierId: string; idempotencyKey: string }
    >(WAGER_ENDPOINTS.FIND_MATCH, FindMatchResponseSchema, {
      gameId,
      tierId,
      idempotencyKey,
    });
    return response.data;
  }

  public async finishRound(roomId: string, moves: string[]): Promise<void> {
    if (moves.length === 1 && moves[0] === '') {
      moves = new Array<string>();
    }

    const NullDataResponseSchema = ApiResponseSchema(NullDataSchema);
    type NullDataResponse = z.infer<typeof NullDataResponseSchema>;

    await this.api.post<NullDataResponse, { roomId: string; moves: string[] }>(
      WAGER_ENDPOINTS.FINISH_ROUND,
      NullDataResponseSchema,
      { roomId, moves },
    );
  }

  public async gameLeaderboards(
    gameId: GameId,
    leaderboardType: LeaderboardType,
    timePeriod: LeaderboardPeriodType,
  ): Promise<GameLeaderboardsData> {
    const GameLeaderboardsResponseSchema = ApiResponseSchema(GameLeaderboardsSchema);
    type GameLeaderboardsResponse = z.infer<typeof GameLeaderboardsResponseSchema>;

    const response = await this.api.get<GameLeaderboardsResponse>(
      `${WAGER_ENDPOINTS.GAME_LEADERBOARDS}/${gameId}/${leaderboardType}/${timePeriod}`,
      GameLeaderboardsResponseSchema,
    );
    return response.data;
  }
}
